import { useEffect, useState, useMemo, useCallback } from 'react';

const DEFAULT_SCROLL__CONFIGURATION = {
  behavior: 'smooth',
} as ScrollToOptions;

type ChatScrollType<T extends HTMLElement> = {
  container?: T;
  condition: boolean;
  treshold?: number;
};

export default function useChatScroll<T extends HTMLElement>(
  args: ChatScrollType<T>,
) {
  const { container, condition, treshold = 0.99 } = args;

  const [shouldScrollTL, setShouldScrollTL] = useState<boolean>(false);

  const showSTL = useMemo(
    () => shouldScrollTL && condition,
    [condition, shouldScrollTL],
  );

  const scrollToMessage = useCallback(
    (messageId: string) => {
      const message = document.querySelector(
        `[data-message-id="${messageId}"]`,
      );

      if (!message) return;
      container.scrollTo({
        ...DEFAULT_SCROLL__CONFIGURATION,
        top: message.scrollTop,
      });
    },
    [container],
  );

  const scrollToBottom = useCallback(() => {
    if (!container) return;

    container.scrollTo({
      ...DEFAULT_SCROLL__CONFIGURATION,
      top: 0,
    });
  }, [container]);

  useEffect(() => {
    if (!container) return;

    const handleOnScroll = (event: Event) => {
      const targetElement = event.target as HTMLElement;

      setShouldScrollTL(
        Math.abs(targetElement.scrollTop) >
          (1 - treshold) * targetElement.scrollHeight,
      );
    };

    container.addEventListener('scroll', handleOnScroll);
    return () => {
      container.removeEventListener('scroll', handleOnScroll);
    };
  }, [container, treshold]);

  return {
    shouldScrollTL,
    showSTL,
    scrollToMessage,
    scrollToBottom,
  };
}
